import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, UserPlus, Clock, Shield, Crown, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import CreateUserModal from '../components/CreateUserModal';
import EditUserModal from '../components/EditUserModal';
import SessionList from '../components/SessionList';
import RoleManager from '../components/RoleManager';
import RoleChangeModal from '../components/RoleChangeModal';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  createdAt: string;
  lastLogin: string | null;
  isEmailVerified: boolean;
  registrationMethod: string;
  roleName: string;
  createdByAdmin: boolean;
  subscriptionType?: 'free' | 'standard' | 'premium';
  subscriptionEndDate?: string | null;
  // 'trial_active' / 'charge_failed' = пользователь с купленной подпиской (триал тоже считается)
  subscriptionStatus?: 'active' | 'trial_active' | 'charge_failed' | 'expired' | 'cancelled' | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const PAGE_LIMIT = 50;

// Статусы, при которых подписка считается купленной (триал — тоже куплена).
const PAID_STATUSES = ['active', 'trial_active', 'charge_failed'];

const isPaidUser = (u: User): boolean =>
  u.subscriptionType !== 'free' &&
  !!u.subscriptionStatus &&
  PAID_STATUSES.includes(u.subscriptionStatus);

// Возвращает дни до окончания подписки (отрицательно — уже истекла), либо null.
const daysUntil = (iso?: string | null): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

// Класс подсветки ячейки «Окончание подписки».
const endDateClass = (u: User): string => {
  if (!u.subscriptionEndDate || !isPaidUser(u)) return 'text-gray-400';
  if (u.subscriptionStatus === 'charge_failed') return 'text-red-700 font-semibold';
  const d = daysUntil(u.subscriptionEndDate);
  if (d === null) return 'text-gray-400';
  if (d < 0) return 'text-red-600 font-semibold';      // истекла
  if (d <= 3) return 'text-amber-600 font-semibold';   // скоро спишется/закончится (включая триал)
  return 'text-emerald-700 font-semibold';             // активна
};

const UsersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'registered' | 'sessions' | 'roles'>('registered');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: PAGE_LIMIT, total: 0, pages: 1 });
  const isFetchingRef = useRef(false);

  const fetchUsers = useCallback(async (targetPage: number, search: string) => {
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      setLoading(true);
      const response = await api.get('/admin/users', {
        params: { search: search || undefined, page: targetPage, limit: PAGE_LIMIT }
      });
      if (response.data.success) {
        setUsers(response.data.users || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  // Debounce input → commit to debouncedSearch and reset to page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, searchTerm ? 400 : 0);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Single fetch source: any change to tab / page / committed search refetches.
  useEffect(() => {
    if (activeTab === 'registered') {
      fetchUsers(page, debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, debouncedSearch]);

  const refresh = useCallback(() => fetchUsers(page, debouncedSearch), [fetchUsers, page, debouncedSearch]);

  const handleCreateUser = () => {
    setShowCreateModal(true);
  };

  const handleUserCreated = () => {
    setShowCreateModal(false);
    refresh();
  };

  const tabs = [
    { id: 'registered' as const, label: 'Зарегистрированные', icon: Users },
    { id: 'sessions' as const, label: 'Сессии', icon: Clock },
    { id: 'roles' as const, label: 'Роли', icon: Shield }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Пользователи</h1>
          <p className="text-gray-600 mt-2">Управление пользователями системы</p>
        </div>
        {activeTab === 'registered' && (
          <button
            onClick={handleCreateUser}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <UserPlus className="h-5 w-5 mr-2" />
            Создать пользователя
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="h-5 w-5 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'registered' && (
          <RegisteredUsersTab
            users={users}
            loading={loading}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onRefresh={refresh}
            pagination={pagination}
            onPageChange={setPage}
          />
        )}
        {activeTab === 'sessions' && <SessionList />}
        {activeTab === 'roles' && <RoleManager />}
      </div>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleUserCreated}
        />
      )}
    </div>
  );
};

interface RegisteredUsersTabProps {
  users: User[];
  loading: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  pagination: Pagination;
  onPageChange: (page: number) => void;
}

const RegisteredUsersTab: React.FC<RegisteredUsersTabProps> = ({
  users,
  loading,
  searchTerm,
  onSearchChange,
  onRefresh,
  pagination,
  onPageChange
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleDeleteUser = async (e: React.MouseEvent, user: User) => {
    e.stopPropagation();
    if (!window.confirm(`Удалить пользователя ${user.fullName || user.email}? Это действие нельзя отменить.`)) return;
    try {
      setDeletingId(user.id);
      await api.delete(`/admin/users/${user.id}`);
      toast.success('Пользователь удалён');
      onRefresh();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Ошибка удаления');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="p-6 border-b border-gray-200">
        <input
          type="text"
          placeholder="Поиск по имени или email..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Имя
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Роль
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Регистрация
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Последний вход
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Метод
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Подписка
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Окончание подписки
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.length === 0 && (
              <tr>
                <td colSpan={10} className="px-6 py-12 text-center text-sm text-gray-500">
                  {searchTerm ? 'Пользователи не найдены' : 'Нет пользователей'}
                </td>
              </tr>
            )}
            {users.map((user) => (
              <tr 
                key={user.id} 
                className="hover:bg-gray-50 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setUserToEdit(user);
                  setShowEditModal(true);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseUp={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {user.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {user.fullName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                    {user.roleName || 'user'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(user.createdAt).toLocaleDateString('ru-RU')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.lastLogin
                    ? new Date(user.lastLogin).toLocaleDateString('ru-RU')
                    : 'Никогда'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.registrationMethod}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                      !isPaidUser(user)
                        ? 'bg-gray-100 text-gray-800'
                        : user.subscriptionType === 'premium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                    }`}>
                      {!isPaidUser(user) ? 'Free' : user.subscriptionType === 'premium' ? 'Premium' : 'Standard'}
                    </span>
                    {isPaidUser(user) && user.subscriptionStatus === 'trial_active' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Триал
                      </span>
                    )}
                    {isPaidUser(user) && user.subscriptionStatus === 'charge_failed' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Сбой оплаты
                      </span>
                    )}
                  </div>
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${endDateClass(user)}`}>
                  {user.subscriptionEndDate
                    ? new Date(user.subscriptionEndDate).toLocaleDateString('ru-RU')
                    : '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setUserToEdit(user);
                      setShowEditModal(true);
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    title="Изменить подписку"
                  >
                    <Crown className="h-4 w-4 mr-1" />
                    Подписка
                  </button>
                  <button
                    onClick={(e) => handleDeleteUser(e, user)}
                    disabled={deletingId === user.id}
                    className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    title="Удалить"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
        <p className="text-sm text-gray-600">
          {pagination.total > 0 ? (
            <>
              Показаны{' '}
              <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>
              {'–'}
              <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
              {' из '}
              <span className="font-medium">{pagination.total}</span>
            </>
          ) : (
            'Нет данных'
          )}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
            disabled={pagination.page <= 1}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Назад
          </button>
          <span className="text-sm text-gray-600">
            Стр. {pagination.page} из {Math.max(1, pagination.pages)}
          </span>
          <button
            onClick={() => onPageChange(Math.min(pagination.pages, pagination.page + 1))}
            disabled={pagination.page >= pagination.pages}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Вперёд
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>

      {showEditModal && userToEdit && (
        <EditUserModal
          isOpen={showEditModal}
          user={userToEdit}
          onClose={() => {
            setShowEditModal(false);
            setUserToEdit(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setUserToEdit(null);
            onRefresh();
          }}
        />
      )}
    </div>
  );
};

export default UsersPage;
